import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import Layout from '../../component/layout';
import { toastify } from '../../../../utils/toast';
import { getItem } from '../../../../utils/localStorage';
import { makeRequest2 } from '../../../../utils/makeRequest';
import {
    get_levy_invoices_raised_report,
    get_levy_payments_received_report,
    get_levy_aging_report,
    get_levy_compliance_report,
    get_levy_collection_vs_budget,
    get_levy_reconciliation_report,
} from '../../../../utils/urls';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

// ─────────────────────────────────────────────────────────────
// LEVY REPORTS — rebuilt to match the card-launcher + modal
// pattern used in FacilityUtilityReports.jsx (water reports).
//
// 6 reports:
//   1. Invoices Raised            — GET_LEVY_INVOICES_RAISED_REPORT_URL   (NEW, backend pending)
//   2. Payments Received          — GET_LEVY_PAYMENTS_RECEIVED_REPORT_URL (NEW, backend pending)
//   3. Outstanding Aging          — get_levy_aging_report                 (existing, real)
//   4. Compliance Report          — get_levy_compliance_report            (existing, real)
//   5. Collection vs Budget       — get_levy_collection_vs_budget         (existing, real)
//   6. Reconciliation Report      — get_levy_reconciliation_report        (existing, real)
//
// Reports 1 & 2 are stubbed the same way "Collected Money" was
// stubbed in FacilityUtilityReports.jsx: state + UI + exports are
// fully wired, the real fetch call is commented out and swapped
// for an empty-result stub until the backend endpoints exist.
// Just uncomment the real call and delete the stub block once
// get_levy_invoices_raised_report / get_levy_payments_received_report
// are live.
// ─────────────────────────────────────────────────────────────

const QUARTERS = [
    { label: 'Q1 (Jan - Mar)', value: 'Q1' },
    { label: 'Q2 (Apr - Jun)', value: 'Q2' },
    { label: 'Q3 (Jul - Sep)', value: 'Q3' },
    { label: 'Q4 (Oct - Dec)', value: 'Q4' },
];

const currentYear = new Date().getFullYear();
const YEARS = [currentYear, currentYear - 1, currentYear - 2];

function emptyPeriod() {
    return {
        periodType: 'dateRange', // 'dateRange' | 'month' | 'quarter'
        startDate: '',
        endDate: '',
        month: '',
        quarter: 'Q1',
        year: String(currentYear),
    };
}

// ── Privacy masking — same rules as CustomersTab.jsx / water reports ──
const maskName = (fullName) => {
    if (!fullName || fullName === 'N/A') return fullName;
    const parts = fullName.trim().split(' ');
    return parts
        .map((part, index) => {
            if (index === 0) return part;
            if (part.length <= 1) return part;
            return part[0] + '*'.repeat(part.length - 1);
        })
        .join(' ');
};

const formatCurrency = (value) =>
    new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(value ?? 0);

/**
 * downloadCsv — generic CSV export for any report's records.
 * columns: [{ header: 'Label', value: (row) => cellValue }]
 */
function downloadCsv(filename, columns, rows) {
    const escape = (v) => {
        const s = v === null || v === undefined ? '' : String(v);
        return (s.includes(',') || s.includes('"') || s.includes('\n'))
            ? `"${s.replace(/"/g, '""')}"`
            : s;
    };
    const header = columns.map((c) => escape(c.header)).join(',');
    const body = rows.map((row) => columns.map((c) => escape(c.value(row))).join(',')).join('\n');
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

// Turns the period filter state into query params understood by the backend.
function periodToParams(period) {
    const params = {};
    if (period.periodType === 'dateRange') {
        if (period.startDate) params.startDate = period.startDate;
        if (period.endDate) params.endDate = period.endDate;
    } else if (period.periodType === 'month') {
        if (period.month) params.month = period.month; // "YYYY-MM"
    } else if (period.periodType === 'quarter') {
        params.quarter = period.quarter;
        params.year = period.year;
    }
    return params;
}

function periodLabel(period) {
    if (period.periodType === 'month') return period.month || 'All time';
    if (period.periodType === 'quarter') return `${period.quarter} ${period.year}`;
    return `${period.startDate || '...'} to ${period.endDate || '...'}`;
}

// Standard PDF header banner used by every export below.
function drawPdfHeader(doc, { title, subtitle, color, generatedRight }) {
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFillColor(...color);
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(title, pageWidth / 2, 18, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(subtitle, pageWidth / 2, 27, { align: 'center' });

    const generatedDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
    doc.setTextColor(55, 65, 81);
    doc.setFontSize(8.5);
    doc.text(`Generated: ${generatedDate}`, 14, 48);
    if (generatedRight) doc.text(generatedRight, pageWidth - 14, 48, { align: 'right' });

    return pageWidth;
}

function drawFooter(doc) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageCount = doc.internal.getNumberOfPages();
    const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
    doc.setFontSize(8);
    doc.setTextColor(130);
    doc.setFont('helvetica', 'italic');
    doc.text('This is a system generated report', pageWidth / 2, pageHeight - 15, { align: 'center' });
    doc.text(`Page ${currentPage} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
}

// ─────────────────────────────────────────────────────────────
// Shared period filter control — Date Range / Month / Quarter
// ─────────────────────────────────────────────────────────────
function PeriodFilter({ period, onChange }) {
    const update = (patch) => onChange({ ...period, ...patch });

    return (
        <div className="row g-2 align-items-end">
            <div className="col-md-3">
                <label className="form-label small text-muted mb-1">Period Type</label>
                <select
                    className="form-control"
                    value={period.periodType}
                    onChange={(e) => update({ periodType: e.target.value })}
                >
                    <option value="dateRange">Date Range</option>
                    <option value="month">Month</option>
                    <option value="quarter">Quarter</option>
                </select>
            </div>

            {period.periodType === 'dateRange' && (
                <>
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Start Date</label>
                        <input
                            type="date"
                            className="form-control"
                            value={period.startDate}
                            onChange={(e) => update({ startDate: e.target.value })}
                        />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">End Date</label>
                        <input
                            type="date"
                            className="form-control"
                            value={period.endDate}
                            onChange={(e) => update({ endDate: e.target.value })}
                        />
                    </div>
                </>
            )}

            {period.periodType === 'month' && (
                <div className="col-md-3">
                    <label className="form-label small text-muted mb-1">Month</label>
                    <input
                        type="month"
                        className="form-control"
                        value={period.month}
                        onChange={(e) => update({ month: e.target.value })}
                    />
                </div>
            )}

            {period.periodType === 'quarter' && (
                <>
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Quarter</label>
                        <select
                            className="form-control"
                            value={period.quarter}
                            onChange={(e) => update({ quarter: e.target.value })}
                        >
                            {QUARTERS.map(q => <option key={q.value} value={q.value}>{q.label}</option>)}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Year</label>
                        <select
                            className="form-control"
                            value={period.year}
                            onChange={(e) => update({ year: e.target.value })}
                        >
                            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                </>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// Reusable modal shell — bootstrap-style, no react-bootstrap dependency
// ─────────────────────────────────────────────────────────────
function ReportModal({ show, onClose, title, subtitle, size = 'modal-xl', children }) {
    if (!show) return null;
    return (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
            <div className={`modal-dialog ${size} modal-dialog-scrollable`}>
                <div className="modal-content">
                    <div className="modal-header">
                        <div>
                            <h5 className="modal-title mb-0">{title}</h5>
                            {subtitle && <small className="text-muted">{subtitle}</small>}
                        </div>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
}

// Export buttons shown once a report has been generated
function ExportBar({ onExportCsv, onExportExcel, onExportPdf, disabled, showCsv = true }) {
    return (
        <div className="d-flex justify-content-end gap-2 mb-3">
            {showCsv && <Button type="button" label="CSV" rounded disabled={disabled} onClick={onExportCsv} />}
            <Button type="button" label="Excel" severity="success" rounded disabled={disabled} onClick={onExportExcel} />
            <Button type="button" label="PDF" severity="warning" rounded disabled={disabled} onClick={onExportPdf} />
        </div>
    );
}

function SummaryCard({ bg, label, value }) {
    return (
        <div className="col-md-3">
            <div className={`card bg-light-${bg}`}>
                <div className="card-body">
                    <h6 className="mb-1">{label}</h6>
                    <h4 className="mb-0">{value}</h4>
                </div>
            </div>
        </div>
    );
}

// A launcher card on the main page — icon, title, blurb, "Generate Report" button
function ReportLauncherCard({ icon, title, description, onGenerate }) {
    return (
        <div className="col-md-4">
            <div className="card h-100">
                <div className="card-body d-flex flex-column">
                    <div className="mb-3">
                        <span className="avtar avtar-s bg-light-primary">
                            <i className={`ti ${icon} f-20`}></i>
                        </span>
                    </div>
                    <h5>{title}</h5>
                    <p className="text-muted flex-grow-1">{description}</p>
                    <button className="btn btn-primary w-100 mt-2" onClick={onGenerate}>
                        <i className="ti ti-report me-1"></i> Generate Report
                    </button>
                </div>
            </div>
        </div>
    );
}

function FacilityLevyReports() {

    // Which modal is open: null | 'invoices' | 'payments' | 'aging' | 'compliance' | 'collection' | 'reconciliation'
    const [activeModal, setActiveModal] = useState(null);
    const closeModal = () => setActiveModal(null);

    // ── Report 1: Invoices Raised (NEW — backend pending) ─────
    const [invoicesPeriod, setInvoicesPeriod] = useState(emptyPeriod());
    const [invoicesSearch, setInvoicesSearch] = useState('');
    const [invoicesStatusFilter, setInvoicesStatusFilter] = useState('All');
    const [invoicesLoading, setInvoicesLoading] = useState(false);
    const [invoicesGenerated, setInvoicesGenerated] = useState(false);
    const [invoicesData, setInvoicesData] = useState([]);
    const [invoicesSummary, setInvoicesSummary] = useState({
        totalInvoices: 0,
        totalInvoicedAmount: 0,
        customersInvoiced: 0,
        paidCount: 0,
        partiallyPaidCount: 0,
        unpaidCount: 0,
    });

    // ── Report 2: Payments Received (NEW — backend pending) ───
    const [paymentsPeriod, setPaymentsPeriod] = useState(emptyPeriod());
    const [paymentsSearch, setPaymentsSearch] = useState('');
    const [paymentsLoading, setPaymentsLoading] = useState(false);
    const [paymentsGenerated, setPaymentsGenerated] = useState(false);
    const [paymentsData, setPaymentsData] = useState([]);
    const [paymentsSummary, setPaymentsSummary] = useState({
        totalCollected: 0,
        paymentCount: 0,
        uniquePayers: 0,
        avgPerPayment: 0,
    });

    // ── Report 3: Outstanding Aging (existing, real) ───────────
    const [agingFilters, setAgingFilters] = useState({
        search: '',
        agingBucket: 'All',
        minAmount: '',
        riskLevel: 'All',
    });
    const [agingLoading, setAgingLoading] = useState(false);
    const [agingGenerated, setAgingGenerated] = useState(false);
    const [agingData, setAgingData] = useState([]);
    const [agingSummary, setAgingSummary] = useState({
        totalOutstanding: 0, current: 0, days30: 0, days60: 0, days90: 0, over90: 0,
        criticalAccounts: 0, highRiskAccounts: 0,
    });

    // ── Report 4: Compliance Report (existing, real) ───────────
    const [complianceFilters, setComplianceFilters] = useState({
        search: '',
        complianceStatus: 'All',
        period: 'Last 12 Months',
        minRate: '',
    });
    const [complianceLoading, setComplianceLoading] = useState(false);
    const [complianceGenerated, setComplianceGenerated] = useState(false);
    const [complianceData, setComplianceData] = useState([]);
    const [complianceSummary, setComplianceSummary] = useState({
        totalContracts: 0, compliantContracts: 0, partiallyCompliant: 0, nonCompliant: 0,
        avgComplianceRate: 0, totalInvoices: 0, paidOnTime: 0,
    });

    // ── Report 5: Collection vs Budget (existing, real) ────────
    const [collectionFilters, setCollectionFilters] = useState({
        search: '',
        levyType: 'All Types',
        period: 'Current Month',
        performanceFilter: 'All',
    });
    const [collectionLoading, setCollectionLoading] = useState(false);
    const [collectionGenerated, setCollectionGenerated] = useState(false);
    const [collectionData, setCollectionData] = useState([]);
    const [collectionSummary, setCollectionSummary] = useState({
        totalBudget: 0, totalCollected: 0, totalVariance: 0, avgCollectionRate: 0,
        onTrackLevies: 0, underperformingLevies: 0,
    });

    // ── Report 6: Reconciliation Report (existing, real) ───────
    const [reconciliationFilters, setReconciliationFilters] = useState({
        search: '',
        status: 'All',
        period: 'Current Month',
        discrepancyOnly: false,
    });
    const [reconciliationLoading, setReconciliationLoading] = useState(false);
    const [reconciliationGenerated, setReconciliationGenerated] = useState(false);
    const [reconciliationData, setReconciliationData] = useState([]);
    const [reconciliationSummary, setReconciliationSummary] = useState({
        totalContracts: 0, matchedContracts: 0, underInvoiced: 0, notInvoiced: 0,
        totalDiscrepancy: 0, expectedRevenue: 0, actualInvoiced: 0,
    });

    // ── Fetch: Report 1 — Invoices Raised (stub, backend pending) ──
    const fetchInvoicesRaisedReport = async () => {
        try {
            setInvoicesLoading(true);
            const facilityId = await getItem('selectedFacilityId');
            const params = new URLSearchParams({
                ...periodToParams(invoicesPeriod),
                ...(invoicesSearch ? { searchQuery: invoicesSearch } : {}),
                ...(invoicesStatusFilter !== 'All' ? { status: invoicesStatusFilter } : {}),
            });

            // TODO: replace with real call once get_levy_invoices_raised_report exists
            // const response = await makeRequest2(
            //     `${get_levy_invoices_raised_report}/${facilityId}?${params.toString()}`,
            //     'GET',
            //     {}
            // );
            // if (response.success) {
            //     setInvoicesData(response.data.data.invoices || []);
            //     setInvoicesSummary(response.data.data.summary || invoicesSummary);
            // } else {
            //     throw new Error(response.message || 'Failed to fetch invoices raised report');
            // }

            setInvoicesData([]);
            setInvoicesSummary({
                totalInvoices: 0, totalInvoicedAmount: 0, customersInvoiced: 0,
                paidCount: 0, partiallyPaidCount: 0, unpaidCount: 0,
            });
            setInvoicesGenerated(true);
        } catch (error) {
            console.log(error.message);
            toastify(error.message, 'error');
        } finally {
            setInvoicesLoading(false);
        }
    };

    // ── Fetch: Report 2 — Payments Received (stub, backend pending) ──
    const fetchPaymentsReceivedReport = async () => {
        try {
            setPaymentsLoading(true);
            const facilityId = await getItem('selectedFacilityId');
            const params = new URLSearchParams({
                ...periodToParams(paymentsPeriod),
                ...(paymentsSearch ? { searchQuery: paymentsSearch } : {}),
            });

            // TODO: replace with real call once get_levy_payments_received_report exists
            // const response = await makeRequest2(
            //     `${get_levy_payments_received_report}/${facilityId}?${params.toString()}`,
            //     'GET',
            //     {}
            // );
            // if (response.success) {
            //     setPaymentsData(response.data.data.payments || []);
            //     setPaymentsSummary(response.data.data.summary || paymentsSummary);
            // } else {
            //     throw new Error(response.message || 'Failed to fetch payments received report');
            // }

            setPaymentsData([]);
            setPaymentsSummary({ totalCollected: 0, paymentCount: 0, uniquePayers: 0, avgPerPayment: 0 });
            setPaymentsGenerated(true);
        } catch (error) {
            console.log(error.message);
            toastify(error.message, 'error');
        } finally {
            setPaymentsLoading(false);
        }
    };

    // ── Fetch: Report 3 — Outstanding Aging (real) ─────────────
    const fetchAgingReport = async () => {
        try {
            setAgingLoading(true);
            const facilityId = await getItem('selectedFacilityId');
            const params = new URLSearchParams();
            if (agingFilters.search) params.append('search', agingFilters.search);
            if (agingFilters.agingBucket !== 'All') params.append('agingBucket', agingFilters.agingBucket);
            if (agingFilters.minAmount) params.append('minAmount', agingFilters.minAmount);
            if (agingFilters.riskLevel !== 'All') params.append('riskLevel', agingFilters.riskLevel);

            const response = await makeRequest2(
                `${get_levy_aging_report}/${facilityId}?${params.toString()}`,
                'GET',
                {}
            );

            if (response.success) {
                setAgingData(response.data.data.aging || []);
                setAgingSummary(response.data.data.summary || {
                    totalOutstanding: 0, current: 0, days30: 0, days60: 0, days90: 0, over90: 0,
                    criticalAccounts: 0, highRiskAccounts: 0,
                });
                setAgingGenerated(true);
            } else {
                throw new Error(response.message || 'Failed to fetch aging report');
            }
        } catch (error) {
            console.log(error.message);
            toastify(error.message, 'error');
        } finally {
            setAgingLoading(false);
        }
    };

    // ── Fetch: Report 4 — Compliance (real) ────────────────────
    const fetchComplianceReport = async () => {
        try {
            setComplianceLoading(true);
            const facilityId = await getItem('selectedFacilityId');
            const params = new URLSearchParams();
            if (complianceFilters.search) params.append('search', complianceFilters.search);
            if (complianceFilters.complianceStatus !== 'All') params.append('complianceStatus', complianceFilters.complianceStatus);
            params.append('period', complianceFilters.period);
            if (complianceFilters.minRate) params.append('minRate', complianceFilters.minRate);

            const response = await makeRequest2(
                `${get_levy_compliance_report}/${facilityId}?${params.toString()}`,
                'GET',
                {}
            );

            if (response.success) {
                setComplianceData(response.data.data.contracts || []);
                setComplianceSummary(response.data.data.summary || {
                    totalContracts: 0, compliantContracts: 0, partiallyCompliant: 0, nonCompliant: 0,
                    avgComplianceRate: 0, totalInvoices: 0, paidOnTime: 0,
                });
                setComplianceGenerated(true);
            } else {
                throw new Error(response.message || 'Failed to fetch compliance report');
            }
        } catch (error) {
            console.log(error.message);
            toastify(error.message, 'error');
        } finally {
            setComplianceLoading(false);
        }
    };

    // ── Fetch: Report 5 — Collection vs Budget (real) ──────────
    const fetchCollectionReport = async () => {
        try {
            setCollectionLoading(true);
            const facilityId = await getItem('selectedFacilityId');
            const params = new URLSearchParams();
            if (collectionFilters.search) params.append('search', collectionFilters.search);
            if (collectionFilters.levyType !== 'All Types') params.append('levyType', collectionFilters.levyType);
            params.append('period', collectionFilters.period);
            if (collectionFilters.performanceFilter !== 'All') params.append('performanceFilter', collectionFilters.performanceFilter);

            const response = await makeRequest2(
                `${get_levy_collection_vs_budget}/${facilityId}?${params.toString()}`,
                'GET',
                {}
            );

            if (response.success) {
                setCollectionData(response.data.data.levies || []);
                setCollectionSummary(response.data.data.summary || {
                    totalBudget: 0, totalCollected: 0, totalVariance: 0, avgCollectionRate: 0,
                    onTrackLevies: 0, underperformingLevies: 0,
                });
                setCollectionGenerated(true);
            } else {
                throw new Error(response.message || 'Failed to fetch collection report');
            }
        } catch (error) {
            console.log(error.message);
            toastify(error.message, 'error');
        } finally {
            setCollectionLoading(false);
        }
    };

    // ── Fetch: Report 6 — Reconciliation (real) ────────────────
    const fetchReconciliationReport = async () => {
        try {
            setReconciliationLoading(true);
            const facilityId = await getItem('selectedFacilityId');
            const params = new URLSearchParams();
            if (reconciliationFilters.search) params.append('search', reconciliationFilters.search);
            if (reconciliationFilters.status !== 'All') params.append('status', reconciliationFilters.status);
            params.append('period', reconciliationFilters.period);
            if (reconciliationFilters.discrepancyOnly) params.append('discrepancyOnly', 'true');

            const response = await makeRequest2(
                `${get_levy_reconciliation_report}/${facilityId}?${params.toString()}`,
                'GET',
                {}
            );

            if (response.success) {
                setReconciliationData(response.data.data.reconciliation || []);
                setReconciliationSummary(response.data.data.summary || {
                    totalContracts: 0, matchedContracts: 0, underInvoiced: 0, notInvoiced: 0,
                    totalDiscrepancy: 0, expectedRevenue: 0, actualInvoiced: 0,
                });
                setReconciliationGenerated(true);
            } else {
                throw new Error(response.message || 'Failed to fetch reconciliation report');
            }
        } catch (error) {
            console.log(error.message);
            toastify(error.message, 'error');
        } finally {
            setReconciliationLoading(false);
        }
    };

    // ═══════════════════════════════════════════════════════════
    // EXPORTS
    // ═══════════════════════════════════════════════════════════

    // ── Report 1: Invoices Raised — exports ────────────────────
    const invoicesTimestamp = () => new Date().toISOString().split('T')[0];

    const exportInvoicesCsv = () => {
        if (invoicesData.length === 0) return;
        downloadCsv(`Invoices_Raised_${invoicesTimestamp()}.csv`, [
            { header: 'Invoice Date', value: (r) => r.invoiceDate ? new Date(r.invoiceDate).toLocaleDateString() : 'N/A' },
            { header: 'Contract', value: (r) => r.contractName },
            { header: 'Unit', value: (r) => r.unitNumber },
            { header: 'Tenant', value: (r) => maskName(r.tenant) },
            { header: 'Levy', value: (r) => r.levyName },
            { header: 'Invoice Amount (KES)', value: (r) => r.invoiceAmount },
            { header: 'Amount Paid (KES)', value: (r) => r.amountPaid },
            { header: 'Balance (KES)', value: (r) => r.balance },
            { header: 'Status', value: (r) => r.status },
        ], invoicesData);
        toastify('Invoices raised report exported successfully', 'success');
    };

    const exportInvoicesExcel = () => {
        if (invoicesData.length === 0) return;
        const excelData = invoicesData.map((r) => ({
            'Invoice Date': r.invoiceDate ? new Date(r.invoiceDate).toLocaleDateString() : 'N/A',
            'Contract': r.contractName,
            'Unit': r.unitNumber,
            'Tenant': maskName(r.tenant),
            'Levy': r.levyName,
            'Invoice Amount (KES)': r.invoiceAmount,
            'Amount Paid (KES)': r.amountPaid,
            'Balance (KES)': r.balance,
            'Status': r.status,
        }));
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(excelData);
        ws['!cols'] = [{ wch: 14 }, { wch: 22 }, { wch: 12 }, { wch: 25 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 14 }, { wch: 16 }];
        XLSX.utils.book_append_sheet(wb, ws, 'Invoices Raised');
        XLSX.writeFile(wb, `Invoices_Raised_${invoicesTimestamp()}.xlsx`);
        toastify('Invoices raised report exported successfully', 'success');
    };

    const exportInvoicesPdf = () => {
        if (invoicesData.length === 0) return;
        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = drawPdfHeader(doc, {
            title: 'INVOICES RAISED REPORT',
            subtitle: `Period: ${periodLabel(invoicesPeriod)}`,
            color: [30, 64, 175],
            generatedRight: `Total Invoices: ${invoicesData.length}`,
        });
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(150, 150, 150);
        doc.text('* Tenant names are partially masked for privacy.', 14, 54);

        const tableRows = invoicesData.map((r) => [
            r.invoiceDate ? new Date(r.invoiceDate).toLocaleDateString() : 'N/A',
            r.contractName || 'N/A',
            r.unitNumber || 'N/A',
            maskName(r.tenant) || 'N/A',
            r.levyName || 'N/A',
            formatCurrency(r.invoiceAmount || 0),
            formatCurrency(r.amountPaid || 0),
            formatCurrency(r.balance || 0),
            r.status || 'N/A',
        ]);

        doc.autoTable({
            startY: 58,
            head: [['Date', 'Contract', 'Unit', 'Tenant', 'Levy', 'Invoiced', 'Paid', 'Balance', 'Status']],
            body: tableRows,
            theme: 'striped',
            headStyles: { fillColor: [30, 64, 175], textColor: 255, fontSize: 7.5, fontStyle: 'bold', halign: 'center' },
            bodyStyles: { fontSize: 7, textColor: [55, 65, 81] },
            alternateRowStyles: { fillColor: [243, 244, 246] },
            columnStyles: { 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right', fontStyle: 'bold' } },
            margin: { left: 10, right: 10 },
            didDrawPage: () => drawFooter(doc),
        });

        doc.save(`Invoices_Raised_${invoicesTimestamp()}.pdf`);
        toastify('Invoices raised report exported successfully', 'success');
    };

    // ── Report 2: Payments Received — exports ──────────────────
    const paymentsTimestamp = () => new Date().toISOString().split('T')[0];

    const exportPaymentsCsv = () => {
        if (paymentsData.length === 0) return;
        downloadCsv(`Payments_Received_${paymentsTimestamp()}.csv`, [
            { header: 'Date', value: (r) => r.paymentDate ? new Date(r.paymentDate).toLocaleDateString() : 'N/A' },
            { header: 'Receipt #', value: (r) => r.receiptNumber },
            { header: 'Contract', value: (r) => r.contractName },
            { header: 'Tenant', value: (r) => maskName(r.tenant) },
            { header: 'Unit', value: (r) => r.unitNumber },
            { header: 'Levy', value: (r) => r.levyName },
            { header: 'Amount (KES)', value: (r) => r.amount },
            { header: 'Method', value: (r) => r.paymentMethod },
        ], paymentsData);
        toastify('Payments received report exported successfully', 'success');
    };

    const exportPaymentsExcel = () => {
        if (paymentsData.length === 0) return;
        const excelData = paymentsData.map((r) => ({
            'Date': r.paymentDate ? new Date(r.paymentDate).toLocaleDateString() : 'N/A',
            'Receipt #': r.receiptNumber,
            'Contract': r.contractName,
            'Tenant': maskName(r.tenant),
            'Unit': r.unitNumber,
            'Levy': r.levyName,
            'Amount (KES)': r.amount,
            'Method': r.paymentMethod,
        }));
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(excelData);
        ws['!cols'] = [{ wch: 14 }, { wch: 16 }, { wch: 22 }, { wch: 25 }, { wch: 12 }, { wch: 18 }, { wch: 16 }, { wch: 16 }];
        XLSX.utils.book_append_sheet(wb, ws, 'Payments Received');
        XLSX.writeFile(wb, `Payments_Received_${paymentsTimestamp()}.xlsx`);
        toastify('Payments received report exported successfully', 'success');
    };

    const exportPaymentsPdf = () => {
        if (paymentsData.length === 0) return;
        const doc = new jsPDF('p', 'mm', 'a4');
        drawPdfHeader(doc, {
            title: 'PAYMENTS RECEIVED REPORT',
            subtitle: `Period: ${periodLabel(paymentsPeriod)}`,
            color: [22, 163, 74],
            generatedRight: `Total Payments: ${paymentsData.length}`,
        });
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(150, 150, 150);
        doc.text('* Tenant names are partially masked for privacy.', 14, 54);

        const tableRows = paymentsData.map((r) => [
            r.paymentDate ? new Date(r.paymentDate).toLocaleDateString() : 'N/A',
            r.receiptNumber || 'N/A',
            r.contractName || 'N/A',
            maskName(r.tenant) || 'N/A',
            r.unitNumber || 'N/A',
            r.levyName || 'N/A',
            formatCurrency(r.amount || 0),
            r.paymentMethod || 'N/A',
        ]);

        doc.autoTable({
            startY: 58,
            head: [['Date', 'Receipt #', 'Contract', 'Tenant', 'Unit', 'Levy', 'Amount', 'Method']],
            body: tableRows,
            theme: 'striped',
            headStyles: { fillColor: [22, 163, 74], textColor: 255, fontSize: 7.5, fontStyle: 'bold', halign: 'center' },
            bodyStyles: { fontSize: 7, textColor: [55, 65, 81] },
            alternateRowStyles: { fillColor: [243, 244, 246] },
            columnStyles: { 6: { halign: 'right', fontStyle: 'bold' } },
            margin: { left: 10, right: 10 },
            didDrawPage: () => drawFooter(doc),
        });

        doc.save(`Payments_Received_${paymentsTimestamp()}.pdf`);
        toastify('Payments received report exported successfully', 'success');
    };

    // ── Report 3: Outstanding Aging — exports ──────────────────
    const agingTimestamp = () => new Date().toISOString().split('T')[0];

    const exportAgingCsv = () => {
        if (agingData.length === 0) return;
        downloadCsv(`Levy_Aging_${agingTimestamp()}.csv`, [
            { header: 'Contract', value: (r) => r.contractName },
            { header: 'Unit #', value: (r) => r.unitNumber },
            { header: 'Tenant', value: (r) => maskName(r.tenant) },
            { header: 'Levy', value: (r) => r.levyName },
            { header: 'Total Outstanding (KES)', value: (r) => r.totalOutstanding },
            { header: 'Current (KES)', value: (r) => r.current },
            { header: '1-30 Days (KES)', value: (r) => r.days30 },
            { header: '31-60 Days (KES)', value: (r) => r.days60 },
            { header: '61-90 Days (KES)', value: (r) => r.days90 },
            { header: 'Over 90 Days (KES)', value: (r) => r.over90 },
            { header: 'Oldest Invoice', value: (r) => r.oldestInvoice ? new Date(r.oldestInvoice).toLocaleDateString() : 'N/A' },
        ], agingData);
        toastify('Aging report exported successfully', 'success');
    };

    const exportAgingExcel = () => {
        if (agingData.length === 0) return;
        const excelData = agingData.map((r) => ({
            'Contract': r.contractName,
            'Unit #': r.unitNumber,
            'Tenant': maskName(r.tenant),
            'Levy': r.levyName,
            'Total Outstanding (KES)': r.totalOutstanding,
            'Current (KES)': r.current,
            '1-30 Days (KES)': r.days30,
            '31-60 Days (KES)': r.days60,
            '61-90 Days (KES)': r.days90,
            'Over 90 Days (KES)': r.over90,
            'Oldest Invoice': r.oldestInvoice ? new Date(r.oldestInvoice).toLocaleDateString() : 'N/A',
        }));
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(excelData);
        ws['!cols'] = [{ wch: 22 }, { wch: 12 }, { wch: 25 }, { wch: 18 }, { wch: 20 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 16 }];
        XLSX.utils.book_append_sheet(wb, ws, 'Aging');
        XLSX.writeFile(wb, `Levy_Aging_${agingTimestamp()}.xlsx`);
        toastify('Aging report exported successfully', 'success');
    };

    const exportAgingPdf = () => {
        if (agingData.length === 0) return;
        const doc = new jsPDF('p', 'mm', 'a4');
        drawPdfHeader(doc, {
            title: 'OUTSTANDING LEVY AGING REPORT',
            subtitle: 'Unpaid levies broken down by age bracket',
            color: [180, 83, 9],
            generatedRight: `Accounts: ${agingData.length}`,
        });
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(150, 150, 150);
        doc.text('* Tenant names are partially masked for privacy.', 14, 54);

        const tableRows = agingData.map((r) => [
            r.contractName || 'N/A',
            r.unitNumber || 'N/A',
            maskName(r.tenant) || 'N/A',
            formatCurrency(r.totalOutstanding || 0),
            formatCurrency(r.current || 0),
            formatCurrency(r.days30 || 0),
            formatCurrency(r.days60 || 0),
            formatCurrency(r.days90 || 0),
            formatCurrency(r.over90 || 0),
        ]);

        doc.autoTable({
            startY: 58,
            head: [['Contract', 'Unit', 'Tenant', 'Total Outstanding', 'Current', '1-30d', '31-60d', '61-90d', '90d+']],
            body: tableRows,
            theme: 'striped',
            headStyles: { fillColor: [180, 83, 9], textColor: 255, fontSize: 7, fontStyle: 'bold', halign: 'center' },
            bodyStyles: { fontSize: 6.5, textColor: [55, 65, 81] },
            alternateRowStyles: { fillColor: [243, 244, 246] },
            columnStyles: { 3: { halign: 'right', fontStyle: 'bold' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' }, 8: { halign: 'right' } },
            margin: { left: 10, right: 10 },
            didDrawPage: () => drawFooter(doc),
        });

        doc.save(`Levy_Aging_${agingTimestamp()}.pdf`);
        toastify('Aging report exported successfully', 'success');
    };

    // ── Report 4: Compliance — exports ─────────────────────────
    const complianceTimestamp = () => new Date().toISOString().split('T')[0];

    const exportComplianceCsv = () => {
        if (complianceData.length === 0) return;
        downloadCsv(`Levy_Compliance_${complianceTimestamp()}.csv`, [
            { header: 'Status', value: (r) => r.status },
            { header: 'Contract', value: (r) => r.contractName },
            { header: 'Unit #', value: (r) => r.unitNumber },
            { header: 'Tenant', value: (r) => maskName(r.tenant) },
            { header: 'Levy', value: (r) => r.levyName },
            { header: 'Compliance Rate (%)', value: (r) => r.complianceRate },
            { header: 'Total Invoices', value: (r) => r.totalInvoices },
            { header: 'Paid On Time', value: (r) => r.paidOnTime },
            { header: 'Paid Late', value: (r) => r.paidLate },
            { header: 'Unpaid', value: (r) => r.unpaid },
        ], complianceData);
        toastify('Compliance report exported successfully', 'success');
    };

    const exportComplianceExcel = () => {
        if (complianceData.length === 0) return;
        const excelData = complianceData.map((r) => ({
            'Status': r.status,
            'Contract': r.contractName,
            'Unit #': r.unitNumber,
            'Tenant': maskName(r.tenant),
            'Levy': r.levyName,
            'Compliance Rate (%)': r.complianceRate,
            'Total Invoices': r.totalInvoices,
            'Paid On Time': r.paidOnTime,
            'Paid Late': r.paidLate,
            'Unpaid': r.unpaid,
        }));
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(excelData);
        ws['!cols'] = [{ wch: 16 }, { wch: 22 }, { wch: 12 }, { wch: 25 }, { wch: 18 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 10 }];
        XLSX.utils.book_append_sheet(wb, ws, 'Compliance');
        XLSX.writeFile(wb, `Levy_Compliance_${complianceTimestamp()}.xlsx`);
        toastify('Compliance report exported successfully', 'success');
    };

    const exportCompliancePdf = () => {
        if (complianceData.length === 0) return;
        const doc = new jsPDF('p', 'mm', 'a4');
        drawPdfHeader(doc, {
            title: 'CONTRIBUTION COMPLIANCE REPORT',
            subtitle: `Period: ${complianceFilters.period}`,
            color: [2, 132, 199],
            generatedRight: `Contracts: ${complianceData.length}`,
        });
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(150, 150, 150);
        doc.text('* Tenant names are partially masked for privacy.', 14, 54);

        const tableRows = complianceData.map((r) => [
            r.status || 'N/A',
            r.contractName || 'N/A',
            r.unitNumber || 'N/A',
            maskName(r.tenant) || 'N/A',
            `${r.complianceRate ?? 0}%`,
            r.paidOnTime ?? 0,
            r.paidLate ?? 0,
            r.unpaid ?? 0,
        ]);

        doc.autoTable({
            startY: 58,
            head: [['Status', 'Contract', 'Unit', 'Tenant', 'Compliance %', 'On Time', 'Late', 'Unpaid']],
            body: tableRows,
            theme: 'striped',
            headStyles: { fillColor: [2, 132, 199], textColor: 255, fontSize: 7.5, fontStyle: 'bold', halign: 'center' },
            bodyStyles: { fontSize: 7, textColor: [55, 65, 81] },
            alternateRowStyles: { fillColor: [243, 244, 246] },
            columnStyles: { 4: { halign: 'right', fontStyle: 'bold' }, 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' } },
            margin: { left: 10, right: 10 },
            didDrawPage: () => drawFooter(doc),
        });

        doc.save(`Levy_Compliance_${complianceTimestamp()}.pdf`);
        toastify('Compliance report exported successfully', 'success');
    };

    // ── Report 5: Collection vs Budget — exports ───────────────
    const collectionTimestamp = () => new Date().toISOString().split('T')[0];

    const exportCollectionCsv = () => {
        if (collectionData.length === 0) return;
        downloadCsv(`Levy_Collection_vs_Budget_${collectionTimestamp()}.csv`, [
            { header: 'Levy Name', value: (r) => r.levyName },
            { header: 'Type', value: (r) => r.levyType },
            { header: 'Budget (KES)', value: (r) => r.budgetAmount },
            { header: 'Collected (KES)', value: (r) => r.collectedAmount },
            { header: 'Variance (KES)', value: (r) => r.variance },
            { header: 'Variance (%)', value: (r) => r.variancePercent },
            { header: 'Collection Rate (%)', value: (r) => r.collectionRate },
            { header: 'Paid Contracts', value: (r) => r.paidContracts },
            { header: 'Total Contracts', value: (r) => r.totalContracts },
        ], collectionData);
        toastify('Collection vs budget report exported successfully', 'success');
    };

    const exportCollectionExcel = () => {
        if (collectionData.length === 0) return;
        const excelData = collectionData.map((r) => ({
            'Levy Name': r.levyName,
            'Type': r.levyType,
            'Budget (KES)': r.budgetAmount,
            'Collected (KES)': r.collectedAmount,
            'Variance (KES)': r.variance,
            'Variance (%)': r.variancePercent,
            'Collection Rate (%)': r.collectionRate,
            'Paid Contracts': r.paidContracts,
            'Total Contracts': r.totalContracts,
        }));
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(excelData);
        ws['!cols'] = [{ wch: 22 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 14 }];
        XLSX.utils.book_append_sheet(wb, ws, 'Collection vs Budget');
        XLSX.writeFile(wb, `Levy_Collection_vs_Budget_${collectionTimestamp()}.xlsx`);
        toastify('Collection vs budget report exported successfully', 'success');
    };

    const exportCollectionPdf = () => {
        if (collectionData.length === 0) return;
        const doc = new jsPDF('p', 'mm', 'a4');
        drawPdfHeader(doc, {
            title: 'LEVY COLLECTION VS BUDGET REPORT',
            subtitle: `Period: ${collectionFilters.period}`,
            color: [101, 65, 168],
            generatedRight: `Levies: ${collectionData.length}`,
        });

        const tableRows = collectionData.map((r) => [
            r.levyName || 'N/A',
            r.levyType || 'N/A',
            formatCurrency(r.budgetAmount || 0),
            formatCurrency(r.collectedAmount || 0),
            formatCurrency(r.variance || 0),
            `${r.collectionRate ?? 0}%`,
            `${r.paidContracts ?? 0}/${r.totalContracts ?? 0}`,
        ]);

        doc.autoTable({
            startY: 54,
            head: [['Levy', 'Type', 'Budget', 'Collected', 'Variance', 'Rate', 'Paid/Total']],
            body: tableRows,
            theme: 'striped',
            headStyles: { fillColor: [101, 65, 168], textColor: 255, fontSize: 7.5, fontStyle: 'bold', halign: 'center' },
            bodyStyles: { fontSize: 7, textColor: [55, 65, 81] },
            alternateRowStyles: { fillColor: [243, 244, 246] },
            columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right', fontStyle: 'bold' }, 5: { halign: 'right' } },
            margin: { left: 10, right: 10 },
            didDrawPage: () => drawFooter(doc),
        });

        doc.save(`Levy_Collection_vs_Budget_${collectionTimestamp()}.pdf`);
        toastify('Collection vs budget report exported successfully', 'success');
    };

    // ── Report 6: Reconciliation — exports ──────────────────────
    const reconciliationTimestamp = () => new Date().toISOString().split('T')[0];

    const exportReconciliationCsv = () => {
        if (reconciliationData.length === 0) return;
        downloadCsv(`Levy_Reconciliation_${reconciliationTimestamp()}.csv`, [
            { header: 'Status', value: (r) => r.status },
            { header: 'Contract', value: (r) => r.contractName },
            { header: 'Unit #', value: (r) => r.unitNumber },
            { header: 'Tenant', value: (r) => maskName(r.tenant) },
            { header: 'Levy', value: (r) => r.levyName },
            { header: 'Period', value: (r) => r.yearMonth },
            { header: 'Contract Amount (KES)', value: (r) => r.contractAmount },
            { header: 'Total Invoiced (KES)', value: (r) => r.totalInvoiced },
            { header: 'Total Paid (KES)', value: (r) => r.totalPaid },
            { header: 'Discrepancy (KES)', value: (r) => r.discrepancy },
            { header: 'Last Invoice', value: (r) => r.lastInvoiceDate === 'Never' ? 'Never' : (r.lastInvoiceDate ? new Date(r.lastInvoiceDate).toLocaleDateString() : 'N/A') },
        ], reconciliationData);
        toastify('Reconciliation report exported successfully', 'success');
    };

    const exportReconciliationExcel = () => {
        if (reconciliationData.length === 0) return;
        const excelData = reconciliationData.map((r) => ({
            'Status': r.status,
            'Contract': r.contractName,
            'Unit #': r.unitNumber,
            'Tenant': maskName(r.tenant),
            'Levy': r.levyName,
            'Period': r.yearMonth,
            'Contract Amount (KES)': r.contractAmount,
            'Total Invoiced (KES)': r.totalInvoiced,
            'Total Paid (KES)': r.totalPaid,
            'Discrepancy (KES)': r.discrepancy,
            'Last Invoice': r.lastInvoiceDate === 'Never' ? 'Never' : (r.lastInvoiceDate ? new Date(r.lastInvoiceDate).toLocaleDateString() : 'N/A'),
        }));
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(excelData);
        ws['!cols'] = [{ wch: 16 }, { wch: 22 }, { wch: 12 }, { wch: 25 }, { wch: 18 }, { wch: 12 }, { wch: 18 }, { wch: 18 }, { wch: 16 }, { wch: 16 }, { wch: 14 }];
        XLSX.utils.book_append_sheet(wb, ws, 'Reconciliation');
        XLSX.writeFile(wb, `Levy_Reconciliation_${reconciliationTimestamp()}.xlsx`);
        toastify('Reconciliation report exported successfully', 'success');
    };

    const exportReconciliationPdf = () => {
        if (reconciliationData.length === 0) return;
        const doc = new jsPDF('p', 'mm', 'a4');
        drawPdfHeader(doc, {
            title: 'LEVY RECONCILIATION REPORT',
            subtitle: `Period: ${reconciliationFilters.period}`,
            color: [55, 65, 81],
            generatedRight: `Contracts: ${reconciliationData.length}`,
        });
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(150, 150, 150);
        doc.text('* Tenant names are partially masked for privacy.', 14, 54);

        const tableRows = reconciliationData.map((r) => [
            r.status || 'N/A',
            r.contractName || 'N/A',
            r.unitNumber || 'N/A',
            maskName(r.tenant) || 'N/A',
            formatCurrency(r.contractAmount || 0),
            formatCurrency(r.totalInvoiced || 0),
            formatCurrency(r.totalPaid || 0),
            formatCurrency(r.discrepancy || 0),
        ]);

        doc.autoTable({
            startY: 58,
            head: [['Status', 'Contract', 'Unit', 'Tenant', 'Contract Amt', 'Invoiced', 'Paid', 'Discrepancy']],
            body: tableRows,
            theme: 'striped',
            headStyles: { fillColor: [55, 65, 81], textColor: 255, fontSize: 7, fontStyle: 'bold', halign: 'center' },
            bodyStyles: { fontSize: 6.5, textColor: [55, 65, 81] },
            alternateRowStyles: { fillColor: [243, 244, 246] },
            columnStyles: { 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right', fontStyle: 'bold' } },
            margin: { left: 10, right: 10 },
            didDrawPage: () => drawFooter(doc),
        });

        doc.save(`Levy_Reconciliation_${reconciliationTimestamp()}.pdf`);
        toastify('Reconciliation report exported successfully', 'success');
    };

    return (
        <Layout>
            <div className="page-header">
                <div className="page-block">
                    <div className="row align-items-center">
                        <div className="col-md-12">
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to={"/facility/"}>Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to={"#"}>Levy Management</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to={"/facility/levy_management/reports"}>Reports</Link>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-body">
                            <h5 className="mb-1">Levy Reports</h5>
                            <small className="text-muted">
                                Generate a report, choose a period, and preview or export it as PDF or Excel.
                            </small>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <ReportLauncherCard
                    icon="ti-file-invoice"
                    title="Invoices Raised"
                    description="How many invoices were raised in a period, the total amount invoiced, how many unique tenants were invoiced, and how many are paid, partially paid, or unpaid."
                    onGenerate={() => setActiveModal('invoices')}
                />
                <ReportLauncherCard
                    icon="ti-cash-banknote"
                    title="Payments Received"
                    description="Every levy payment received in a period, with receipt numbers, method, and running collection totals per tenant."
                    onGenerate={() => setActiveModal('payments')}
                />
                <ReportLauncherCard
                    icon="ti-calendar-time"
                    title="Outstanding Aging"
                    description="Unpaid levy invoices broken down by age bracket (current, 1-30, 31-60, 61-90, 90+ days) with risk flags."
                    onGenerate={() => setActiveModal('aging')}
                />
                <ReportLauncherCard
                    icon="ti-checkbox"
                    title="Compliance Report"
                    description="Which tenants/units are paying levies on time — compliance rate per contract over a chosen period."
                    onGenerate={() => setActiveModal('compliance')}
                />
                <ReportLauncherCard
                    icon="ti-chart-bar"
                    title="Collection vs Budget"
                    description="Actual levy collections against budgeted amounts, by levy type — flags underperforming levies."
                    onGenerate={() => setActiveModal('collection')}
                />
                <ReportLauncherCard
                    icon="ti-file-check"
                    title="Reconciliation"
                    description="Matches invoices to contracts to surface under-invoiced or never-invoiced units, with the resulting revenue discrepancy."
                    onGenerate={() => setActiveModal('reconciliation')}
                />
            </div>

            {/* ── MODAL 1: Invoices Raised ─────────────────────────── */}
            <ReportModal
                show={activeModal === 'invoices'}
                onClose={closeModal}
                title="Invoices Raised Report"
                subtitle="Total invoices raised in a period, who was invoiced, and paid/unpaid breakdown"
            >
                <div className="alert alert-info mb-3">
                    <i className="ti ti-info-circle me-2"></i>
                    Backend for this report is still being built — Generate will run once it's ready.
                </div>

                <div className="row g-2 mb-3">
                    <div className="col-md-7">
                        <PeriodFilter period={invoicesPeriod} onChange={setInvoicesPeriod} />
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small text-muted mb-1">Status</label>
                        <select
                            className="form-control"
                            value={invoicesStatusFilter}
                            onChange={(e) => setInvoicesStatusFilter(e.target.value)}
                        >
                            <option>All</option>
                            <option>Paid</option>
                            <option>Partially Paid</option>
                            <option>Unpaid</option>
                        </select>
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Search Tenant/Unit</label>
                        <input
                            className="form-control"
                            placeholder="Tenant, unit, contract..."
                            value={invoicesSearch}
                            onChange={(e) => setInvoicesSearch(e.target.value)}
                        />
                    </div>
                </div>

                <div className="d-flex justify-content-end mb-3">
                    <button className="btn btn-primary" onClick={fetchInvoicesRaisedReport} disabled={invoicesLoading}>
                        <i className="ti ti-refresh me-1"></i> {invoicesLoading ? 'Generating...' : 'Generate'}
                    </button>
                </div>

                {invoicesGenerated && (
                    <>
                        <div className="row mb-2">
                            <SummaryCard bg="primary" label="Total Invoices" value={invoicesSummary.totalInvoices} />
                            <SummaryCard bg="info" label="Total Invoiced" value={formatCurrency(invoicesSummary.totalInvoicedAmount)} />
                            <SummaryCard bg="secondary" label="Tenants Invoiced" value={invoicesSummary.customersInvoiced} />
                            <SummaryCard bg="success" label="Fully Paid" value={invoicesSummary.paidCount} />
                        </div>
                        <div className="row mb-3">
                            <SummaryCard bg="warning" label="Partially Paid" value={invoicesSummary.partiallyPaidCount} />
                            <SummaryCard bg="danger" label="Unpaid" value={invoicesSummary.unpaidCount} />
                        </div>

                        <ExportBar
                            disabled={invoicesData.length === 0}
                            onExportCsv={exportInvoicesCsv}
                            onExportExcel={exportInvoicesExcel}
                            onExportPdf={exportInvoicesPdf}
                        />

                        <DataTable
                            value={invoicesData}
                            emptyMessage="No invoices found for this period."
                            sortMode="multiple"
                            paginator
                            rows={10}
                            stripedRows
                            tableStyle={{ minWidth: '50rem' }}
                            loading={invoicesLoading}
                        >
                            <Column field="invoiceDate" sortable header="Invoice Date" body={(r) => r.invoiceDate ? new Date(r.invoiceDate).toLocaleDateString() : 'N/A'}></Column>
                            <Column field="contractName" sortable header="Contract"></Column>
                            <Column field="unitNumber" sortable header="Unit"></Column>
                            <Column field="tenant" sortable header="Tenant" body={(r) => maskName(r.tenant) || 'N/A'}></Column>
                            <Column field="levyName" sortable header="Levy"></Column>
                            <Column field="invoiceAmount" sortable header="Invoiced" body={(r) => formatCurrency(r.invoiceAmount || 0)}></Column>
                            <Column field="amountPaid" sortable header="Paid" body={(r) => formatCurrency(r.amountPaid || 0)}></Column>
                            <Column field="balance" sortable header="Balance" body={(r) => (
                                <span className={r.balance > 0 ? 'text-danger fw-bold' : 'text-success'}>{formatCurrency(r.balance || 0)}</span>
                            )}></Column>
                            <Column field="status" header="Status" body={(r) => (
                                <span className={`badge ${
                                    r.status === 'Paid' ? 'bg-success text-light' :
                                    r.status === 'Partially Paid' ? 'bg-warning text-light' :
                                    'bg-danger text-light'
                                }`}>
                                    {r.status}
                                </span>
                            )}></Column>
                        </DataTable>
                    </>
                )}
            </ReportModal>

            {/* ── MODAL 2: Payments Received ───────────────────────── */}
            <ReportModal
                show={activeModal === 'payments'}
                onClose={closeModal}
                title="Payments Received Report"
                subtitle="Every levy payment received in a chosen period"
            >
                <div className="alert alert-info mb-3">
                    <i className="ti ti-info-circle me-2"></i>
                    Backend for this report is still being built — Generate will run once it's ready.
                </div>

                <div className="row g-2 mb-3">
                    <div className="col-md-9">
                        <PeriodFilter period={paymentsPeriod} onChange={setPaymentsPeriod} />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Search Tenant/Receipt</label>
                        <input
                            className="form-control"
                            placeholder="Tenant, receipt #, unit..."
                            value={paymentsSearch}
                            onChange={(e) => setPaymentsSearch(e.target.value)}
                        />
                    </div>
                </div>

                <div className="d-flex justify-content-end mb-3">
                    <button className="btn btn-primary" onClick={fetchPaymentsReceivedReport} disabled={paymentsLoading}>
                        <i className="ti ti-refresh me-1"></i> {paymentsLoading ? 'Generating...' : 'Generate'}
                    </button>
                </div>

                {paymentsGenerated && (
                    <>
                        <div className="row mb-3">
                            <SummaryCard bg="success" label="Total Collected" value={formatCurrency(paymentsSummary.totalCollected)} />
                            <SummaryCard bg="primary" label="Payments" value={paymentsSummary.paymentCount} />
                            <SummaryCard bg="info" label="Unique Payers" value={paymentsSummary.uniquePayers} />
                            <SummaryCard bg="warning" label="Avg / Payment" value={formatCurrency(paymentsSummary.avgPerPayment)} />
                        </div>

                        <ExportBar
                            disabled={paymentsData.length === 0}
                            onExportCsv={exportPaymentsCsv}
                            onExportExcel={exportPaymentsExcel}
                            onExportPdf={exportPaymentsPdf}
                        />

                        <DataTable
                            value={paymentsData}
                            emptyMessage="No payments found for this period."
                            sortMode="multiple"
                            paginator
                            rows={10}
                            stripedRows
                            tableStyle={{ minWidth: '50rem' }}
                            loading={paymentsLoading}
                        >
                            <Column field="paymentDate" sortable header="Date" body={(r) => r.paymentDate ? new Date(r.paymentDate).toLocaleDateString() : 'N/A'}></Column>
                            <Column field="receiptNumber" header="Receipt #"></Column>
                            <Column field="contractName" sortable header="Contract"></Column>
                            <Column field="tenant" sortable header="Tenant" body={(r) => maskName(r.tenant) || 'N/A'}></Column>
                            <Column field="unitNumber" header="Unit"></Column>
                            <Column field="levyName" header="Levy"></Column>
                            <Column field="amount" sortable header="Amount" body={(r) => formatCurrency(r.amount || 0)}></Column>
                            <Column field="paymentMethod" header="Method"></Column>
                        </DataTable>
                    </>
                )}
            </ReportModal>

            {/* ── MODAL 3: Outstanding Aging ───────────────────────── */}
            <ReportModal
                show={activeModal === 'aging'}
                onClose={closeModal}
                title="Outstanding Levy Aging Report"
                subtitle="Analyze unpaid levies by age brackets"
            >
                <div className="row g-2 mb-3">
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Search</label>
                        <input
                            className="form-control"
                            placeholder="Search tenant, unit..."
                            value={agingFilters.search}
                            onChange={(e) => setAgingFilters({ ...agingFilters, search: e.target.value })}
                        />
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small text-muted mb-1">Aging Bucket</label>
                        <select
                            className="form-control"
                            value={agingFilters.agingBucket}
                            onChange={(e) => setAgingFilters({ ...agingFilters, agingBucket: e.target.value })}
                        >
                            <option>All</option>
                            <option>Current</option>
                            <option>1-30 Days</option>
                            <option>31-60 Days</option>
                            <option>61-90 Days</option>
                            <option>Over 90 Days</option>
                        </select>
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small text-muted mb-1">Min Amount</label>
                        <input
                            className="form-control"
                            type="number"
                            placeholder="Min Amount"
                            value={agingFilters.minAmount}
                            onChange={(e) => setAgingFilters({ ...agingFilters, minAmount: e.target.value })}
                        />
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small text-muted mb-1">Risk Level</label>
                        <select
                            className="form-control"
                            value={agingFilters.riskLevel}
                            onChange={(e) => setAgingFilters({ ...agingFilters, riskLevel: e.target.value })}
                        >
                            <option>All</option>
                            <option>Critical</option>
                            <option>High Risk</option>
                        </select>
                    </div>
                </div>

                <div className="d-flex justify-content-end mb-3">
                    <button className="btn btn-primary" onClick={fetchAgingReport} disabled={agingLoading}>
                        <i className="ti ti-refresh me-1"></i> {agingLoading ? 'Generating...' : 'Generate'}
                    </button>
                </div>

                {agingGenerated && (
                    <>
                        <div className="row mb-3">
                            <div className="col-md-2">
                                <div className="card bg-light-danger">
                                    <div className="card-body">
                                        <h6 className="mb-1">Total Outstanding</h6>
                                        <h5 className="mb-0">{formatCurrency(agingSummary.totalOutstanding)}</h5>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="card bg-light-success">
                                    <div className="card-body">
                                        <h6 className="mb-1">Current</h6>
                                        <h5 className="mb-0">{formatCurrency(agingSummary.current)}</h5>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="card bg-light-info">
                                    <div className="card-body">
                                        <h6 className="mb-1">1-30 Days</h6>
                                        <h5 className="mb-0">{formatCurrency(agingSummary.days30)}</h5>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="card bg-light-warning">
                                    <div className="card-body">
                                        <h6 className="mb-1">31-60 Days</h6>
                                        <h5 className="mb-0">{formatCurrency(agingSummary.days60)}</h5>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="card bg-light-warning">
                                    <div className="card-body">
                                        <h6 className="mb-1">61-90 Days</h6>
                                        <h5 className="mb-0">{formatCurrency(agingSummary.days90)}</h5>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="card bg-light-danger">
                                    <div className="card-body">
                                        <h6 className="mb-1">Over 90 Days</h6>
                                        <h5 className="mb-0">{formatCurrency(agingSummary.over90)}</h5>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <ExportBar
                            disabled={agingData.length === 0}
                            onExportCsv={exportAgingCsv}
                            onExportExcel={exportAgingExcel}
                            onExportPdf={exportAgingPdf}
                        />

                        <DataTable
                            value={agingData}
                            emptyMessage="No aging data found."
                            sortMode="multiple"
                            paginator
                            rows={10}
                            stripedRows
                            tableStyle={{ minWidth: '50rem' }}
                            loading={agingLoading}
                        >
                            <Column field="contractName" sortable header="Contract"></Column>
                            <Column field="unitNumber" sortable header="Unit #"></Column>
                            <Column field="tenant" sortable header="Tenant" body={(r) => maskName(r.tenant) || 'N/A'}></Column>
                            <Column field="levyName" sortable header="Levy"></Column>
                            <Column field="totalOutstanding" sortable header="Total Outstanding" body={(r) => (
                                <span className="fw-bold text-danger">{formatCurrency(r.totalOutstanding || 0)}</span>
                            )}></Column>
                            <Column field="current" header="Current" body={(r) => formatCurrency(r.current || 0)}></Column>
                            <Column field="days30" header="1-30 Days" body={(r) => formatCurrency(r.days30 || 0)}></Column>
                            <Column field="days60" header="31-60 Days" body={(r) => formatCurrency(r.days60 || 0)}></Column>
                            <Column field="days90" header="61-90 Days" body={(r) => formatCurrency(r.days90 || 0)}></Column>
                            <Column field="over90" header="Over 90 Days" body={(r) => (
                                <span className="text-danger fw-bold">{formatCurrency(r.over90 || 0)}</span>
                            )}></Column>
                            <Column field="oldestInvoice" sortable header="Oldest Invoice" body={(r) => r.oldestInvoice ? new Date(r.oldestInvoice).toLocaleDateString() : 'N/A'}></Column>
                        </DataTable>
                    </>
                )}
            </ReportModal>

            {/* ── MODAL 4: Compliance ──────────────────────────────── */}
            <ReportModal
                show={activeModal === 'compliance'}
                onClose={closeModal}
                title="Contribution Compliance Report"
                subtitle="Monitor which tenants/units are paying levies on time"
            >
                <div className="row g-2 mb-3">
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Search</label>
                        <input
                            className="form-control"
                            placeholder="Search tenant, unit..."
                            value={complianceFilters.search}
                            onChange={(e) => setComplianceFilters({ ...complianceFilters, search: e.target.value })}
                        />
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small text-muted mb-1">Status</label>
                        <select
                            className="form-control"
                            value={complianceFilters.complianceStatus}
                            onChange={(e) => setComplianceFilters({ ...complianceFilters, complianceStatus: e.target.value })}
                        >
                            <option>All</option>
                            <option>Compliant</option>
                            <option>Partially Compliant</option>
                            <option>Non-Compliant</option>
                        </select>
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small text-muted mb-1">Period</label>
                        <select
                            className="form-control"
                            value={complianceFilters.period}
                            onChange={(e) => setComplianceFilters({ ...complianceFilters, period: e.target.value })}
                        >
                            <option>Last 12 Months</option>
                            <option>Last 6 Months</option>
                            <option>Last 3 Months</option>
                            <option>Current Year</option>
                        </select>
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small text-muted mb-1">Min Rate %</label>
                        <input
                            className="form-control"
                            type="number"
                            placeholder="Min Rate %"
                            value={complianceFilters.minRate}
                            onChange={(e) => setComplianceFilters({ ...complianceFilters, minRate: e.target.value })}
                        />
                    </div>
                </div>

                <div className="d-flex justify-content-end mb-3">
                    <button className="btn btn-primary" onClick={fetchComplianceReport} disabled={complianceLoading}>
                        <i className="ti ti-refresh me-1"></i> {complianceLoading ? 'Generating...' : 'Generate'}
                    </button>
                </div>

                {complianceGenerated && (
                    <>
                        <div className="row mb-3">
                            <SummaryCard bg="primary" label="Total Contracts" value={complianceSummary.totalContracts} />
                            <SummaryCard bg="success" label="Compliant" value={complianceSummary.compliantContracts} />
                            <SummaryCard bg="warning" label="Partially Compliant" value={complianceSummary.partiallyCompliant} />
                            <SummaryCard bg="danger" label="Non-Compliant" value={complianceSummary.nonCompliant} />
                        </div>

                        <ExportBar
                            disabled={complianceData.length === 0}
                            onExportCsv={exportComplianceCsv}
                            onExportExcel={exportComplianceExcel}
                            onExportPdf={exportCompliancePdf}
                        />

                        <DataTable
                            value={complianceData}
                            emptyMessage="No compliance data found."
                            sortMode="multiple"
                            paginator
                            rows={10}
                            stripedRows
                            tableStyle={{ minWidth: '50rem' }}
                            loading={complianceLoading}
                        >
                            <Column field="status" sortable header="Status" body={(r) => (
                                <span className={`badge ${
                                    r.status === 'Compliant' ? 'bg-success text-light' :
                                    r.status === 'Partially Compliant' ? 'bg-warning text-light' :
                                    'bg-danger text-light'
                                }`}>
                                    {r.status}
                                </span>
                            )}></Column>
                            <Column field="contractName" sortable header="Contract"></Column>
                            <Column field="unitNumber" sortable header="Unit #"></Column>
                            <Column field="tenant" sortable header="Tenant" body={(r) => maskName(r.tenant) || 'N/A'}></Column>
                            <Column field="levyName" sortable header="Levy"></Column>
                            <Column field="complianceRate" sortable header="Compliance Rate" body={(r) => (
                                <div>
                                    <div className="progress" style={{ height: '20px' }}>
                                        <div
                                            className={`progress-bar ${
                                                r.complianceRate >= 80 ? 'bg-success' :
                                                r.complianceRate >= 50 ? 'bg-warning' :
                                                'bg-danger'
                                            }`}
                                            style={{ width: `${r.complianceRate}%` }}
                                        >
                                            {r.complianceRate}%
                                        </div>
                                    </div>
                                </div>
                            )}></Column>
                            <Column field="totalInvoices" header="Total Invoices"></Column>
                            <Column field="paidOnTime" sortable header="Paid On Time" body={(r) => <span className="text-success fw-bold">{r.paidOnTime}</span>}></Column>
                            <Column field="paidLate" header="Paid Late" body={(r) => <span className="text-warning">{r.paidLate}</span>}></Column>
                            <Column field="unpaid" header="Unpaid" body={(r) => <span className="text-danger fw-bold">{r.unpaid}</span>}></Column>
                            <Column field="avgDaysLate" sortable header="Avg Days Late"></Column>
                            <Column field="lastPaymentDate" sortable header="Last Payment" body={(r) => r.lastPaymentDate ? new Date(r.lastPaymentDate).toLocaleDateString() : 'N/A'}></Column>
                        </DataTable>
                    </>
                )}
            </ReportModal>

            {/* ── MODAL 5: Collection vs Budget ────────────────────── */}
            <ReportModal
                show={activeModal === 'collection'}
                onClose={closeModal}
                title="Levy Collection vs Budget Report"
                subtitle="Track actual levy collections against budgeted amounts"
            >
                <div className="row g-2 mb-3">
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Search</label>
                        <input
                            className="form-control"
                            placeholder="Search levy name..."
                            value={collectionFilters.search}
                            onChange={(e) => setCollectionFilters({ ...collectionFilters, search: e.target.value })}
                        />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Levy Type</label>
                        <select
                            className="form-control"
                            value={collectionFilters.levyType}
                            onChange={(e) => setCollectionFilters({ ...collectionFilters, levyType: e.target.value })}
                        >
                            <option>All Types</option>
                            <option>Service Charge</option>
                            <option>Utilities</option>
                            <option>Maintenance</option>
                        </select>
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Period</label>
                        <select
                            className="form-control"
                            value={collectionFilters.period}
                            onChange={(e) => setCollectionFilters({ ...collectionFilters, period: e.target.value })}
                        >
                            <option>Current Month</option>
                            <option>Last Month</option>
                            <option>Quarter</option>
                            <option>Year to Date</option>
                        </select>
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Performance</label>
                        <select
                            className="form-control"
                            value={collectionFilters.performanceFilter}
                            onChange={(e) => setCollectionFilters({ ...collectionFilters, performanceFilter: e.target.value })}
                        >
                            <option>All</option>
                            <option>On Track</option>
                            <option>Underperforming</option>
                        </select>
                    </div>
                </div>

                <div className="d-flex justify-content-end mb-3">
                    <button className="btn btn-primary" onClick={fetchCollectionReport} disabled={collectionLoading}>
                        <i className="ti ti-refresh me-1"></i> {collectionLoading ? 'Generating...' : 'Generate'}
                    </button>
                </div>

                {collectionGenerated && (
                    <>
                        <div className="row mb-3">
                            <SummaryCard bg="primary" label="Total Budget" value={formatCurrency(collectionSummary.totalBudget)} />
                            <SummaryCard bg="success" label="Total Collected" value={formatCurrency(collectionSummary.totalCollected)} />
                            <SummaryCard bg="warning" label="Variance" value={formatCurrency(collectionSummary.totalVariance)} />
                            <SummaryCard bg="info" label="Avg Collection Rate" value={`${collectionSummary.avgCollectionRate}%`} />
                        </div>

                        <ExportBar
                            disabled={collectionData.length === 0}
                            onExportCsv={exportCollectionCsv}
                            onExportExcel={exportCollectionExcel}
                            onExportPdf={exportCollectionPdf}
                        />

                        <DataTable
                            value={collectionData}
                            emptyMessage="No collection data found."
                            sortMode="multiple"
                            paginator
                            rows={10}
                            stripedRows
                            tableStyle={{ minWidth: '50rem' }}
                            loading={collectionLoading}
                        >
                            <Column field="levyName" sortable header="Levy Name"></Column>
                            <Column field="levyType" sortable header="Type"></Column>
                            <Column field="budgetAmount" sortable header="Budget" body={(r) => formatCurrency(r.budgetAmount || 0)}></Column>
                            <Column field="collectedAmount" sortable header="Collected" body={(r) => formatCurrency(r.collectedAmount || 0)}></Column>
                            <Column field="variance" sortable header="Variance" body={(r) => (
                                <span className={r.variance < 0 ? 'text-danger' : 'text-success'}>{formatCurrency(r.variance || 0)}</span>
                            )}></Column>
                            <Column field="variancePercent" sortable header="Variance %" body={(r) => (
                                <span className={r.variancePercent < 0 ? 'text-danger' : 'text-success'}>{r.variancePercent}%</span>
                            )}></Column>
                            <Column field="collectionRate" sortable header="Collection Rate" body={(r) => (
                                <div>
                                    <div className="progress" style={{ height: '20px' }}>
                                        <div
                                            className={`progress-bar ${r.collectionRate >= 90 ? 'bg-success' : r.collectionRate >= 70 ? 'bg-warning' : 'bg-danger'}`}
                                            style={{ width: `${r.collectionRate}%` }}
                                        >
                                            {r.collectionRate}%
                                        </div>
                                    </div>
                                </div>
                            )}></Column>
                            <Column field="paidContracts" header="Paid/Total" body={(r) => `${r.paidContracts}/${r.totalContracts}`}></Column>
                        </DataTable>
                    </>
                )}
            </ReportModal>

            {/* ── MODAL 6: Reconciliation ──────────────────────────── */}
            <ReportModal
                show={activeModal === 'reconciliation'}
                onClose={closeModal}
                title="Levy Reconciliation Report"
                subtitle="Match invoices to contracts and identify discrepancies"
            >
                <div className="row g-2 mb-3">
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Search</label>
                        <input
                            className="form-control"
                            placeholder="Search tenant, unit..."
                            value={reconciliationFilters.search}
                            onChange={(e) => setReconciliationFilters({ ...reconciliationFilters, search: e.target.value })}
                        />
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small text-muted mb-1">Status</label>
                        <select
                            className="form-control"
                            value={reconciliationFilters.status}
                            onChange={(e) => setReconciliationFilters({ ...reconciliationFilters, status: e.target.value })}
                        >
                            <option>All</option>
                            <option>Matched</option>
                            <option>Under-Invoiced</option>
                            <option>Not Invoiced</option>
                        </select>
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small text-muted mb-1">Period</label>
                        <select
                            className="form-control"
                            value={reconciliationFilters.period}
                            onChange={(e) => setReconciliationFilters({ ...reconciliationFilters, period: e.target.value })}
                        >
                            <option>Current Month</option>
                            <option>Last Month</option>
                            <option>Last 3 Months</option>
                            <option>Last 6 Months</option>
                        </select>
                    </div>
                    <div className="col-md-2">
                        <div className="form-check mt-4">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="discrepancyOnly"
                                checked={reconciliationFilters.discrepancyOnly}
                                onChange={(e) => setReconciliationFilters({ ...reconciliationFilters, discrepancyOnly: e.target.checked })}
                            />
                            <label className="form-check-label" htmlFor="discrepancyOnly">
                                Discrepancies Only
                            </label>
                        </div>
                    </div>
                </div>

                <div className="d-flex justify-content-end mb-3">
                    <button className="btn btn-primary" onClick={fetchReconciliationReport} disabled={reconciliationLoading}>
                        <i className="ti ti-refresh me-1"></i> {reconciliationLoading ? 'Generating...' : 'Generate'}
                    </button>
                </div>

                {reconciliationGenerated && (
                    <>
                        <div className="row mb-3">
                            <SummaryCard bg="primary" label="Total Contracts" value={reconciliationSummary.totalContracts} />
                            <SummaryCard bg="success" label="Matched" value={reconciliationSummary.matchedContracts} />
                            <SummaryCard bg="warning" label="Under-Invoiced" value={reconciliationSummary.underInvoiced} />
                            <SummaryCard bg="danger" label="Not Invoiced" value={reconciliationSummary.notInvoiced} />
                        </div>

                        <div className="row mb-3">
                            <div className="col-md-4">
                                <div className="card bg-light-info">
                                    <div className="card-body">
                                        <h6 className="mb-1">Expected Revenue</h6>
                                        <h4 className="mb-0">{formatCurrency(reconciliationSummary.expectedRevenue)}</h4>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="card bg-light-success">
                                    <div className="card-body">
                                        <h6 className="mb-1">Actual Invoiced</h6>
                                        <h4 className="mb-0">{formatCurrency(reconciliationSummary.actualInvoiced)}</h4>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="card bg-light-danger">
                                    <div className="card-body">
                                        <h6 className="mb-1">Total Discrepancy</h6>
                                        <h4 className={`mb-0 ${reconciliationSummary.totalDiscrepancy < 0 ? 'text-danger' : 'text-success'}`}>
                                            {formatCurrency(reconciliationSummary.totalDiscrepancy)}
                                        </h4>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <ExportBar
                            disabled={reconciliationData.length === 0}
                            onExportCsv={exportReconciliationCsv}
                            onExportExcel={exportReconciliationExcel}
                            onExportPdf={exportReconciliationPdf}
                        />

                        <DataTable
                            value={reconciliationData}
                            emptyMessage="No reconciliation data found."
                            sortMode="multiple"
                            paginator
                            rows={10}
                            stripedRows
                            tableStyle={{ minWidth: '50rem' }}
                            loading={reconciliationLoading}
                        >
                            <Column field="status" sortable header="Status" body={(r) => (
                                <span className={`badge ${
                                    r.status === 'Matched' ? 'bg-success text-light' :
                                    r.status === 'Under-Invoiced' ? 'bg-warning text-light' :
                                    'bg-danger text-light'
                                }`}>
                                    {r.status}
                                </span>
                            )}></Column>
                            <Column field="contractName" sortable header="Contract"></Column>
                            <Column field="unitNumber" sortable header="Unit #"></Column>
                            <Column field="tenant" sortable header="Tenant" body={(r) => maskName(r.tenant) || 'N/A'}></Column>
                            <Column field="levyName" sortable header="Levy"></Column>
                            <Column field="yearMonth" sortable header="Period"></Column>
                            <Column field="contractAmount" sortable header="Contract Amount" body={(r) => formatCurrency(r.contractAmount || 0)}></Column>
                            <Column field="totalInvoiced" sortable header="Total Invoiced" body={(r) => formatCurrency(r.totalInvoiced || 0)}></Column>
                            <Column field="totalPaid" sortable header="Total Paid" body={(r) => formatCurrency(r.totalPaid || 0)}></Column>
                            <Column field="discrepancy" sortable header="Discrepancy" body={(r) => (
                                <span className={`fw-bold ${r.discrepancy < 0 ? 'text-danger' : r.discrepancy > 0 ? 'text-success' : 'text-muted'}`}>
                                    {formatCurrency(r.discrepancy || 0)}
                                </span>
                            )}></Column>
                            <Column field="invoiceCount" header="Invoice Count"></Column>
                            <Column field="lastInvoiceDate" sortable header="Last Invoice" body={(r) =>
                                r.lastInvoiceDate === 'Never' ?
                                <span className="text-danger">Never</span> :
                                (r.lastInvoiceDate ? new Date(r.lastInvoiceDate).toLocaleDateString() : 'N/A')
                            }></Column>
                        </DataTable>
                    </>
                )}
            </ReportModal>

        </Layout>
    );
}

export default FacilityLevyReports;